import { useEffect, useState } from "react";

import { Combobox, type ComboOption } from "@/components/cinch/Combobox";
import { NumberInput } from "@/components/cinch/NumberInput";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import { useItemWrites, usePriceMetrics, type ItemRow } from "../api";
import { ITEM_CATEGORIES } from "../catalog";
import { Field, writeError } from "./editorCommon";

/** Create (item === null) or edit (item === ItemRow) a billing catalog item.
 *  Writes proxy to Cinch (superuser-gated); integration_keys are free-form public
 *  keys entered comma-separated (Cinch has no key catalog endpoint to pick from). */
export function ItemEditor({
  open,
  item,
  onClose,
}: {
  open: boolean;
  item: ItemRow | null;
  onClose: () => void;
}) {
  const { save } = useItemWrites();
  const metricsQ = usePriceMetrics({ limit: 500, offset: 0 });
  const metricOptions: ComboOption[] = (metricsQ.data?.results ?? []).map((m) => ({
    id: m.id,
    label: m.name || m.id,
  }));

  const [name, setName] = useState("");
  const [category, setCategory] = useState("other");
  const [priceMetric, setPriceMetric] = useState<string | null>(null);
  const [integrations, setIntegrations] = useState("");
  const [unitCost, setUnitCost] = useState<number | null>(null);
  const [floor, setFloor] = useState<number | null>(null);
  const [starting, setStarting] = useState<number | null>(null);
  const [recommended, setRecommended] = useState<number | null>(null);
  const [sku, setSku] = useState("");
  const [descr, setDescr] = useState("");
  const [custDescr, setCustDescr] = useState("");

  // Rehydrate the form each time the dialog opens for a (different) record.
  useEffect(() => {
    if (!open) return;
    setName(item?.name ?? "");
    setCategory(item?.category ?? "other");
    setPriceMetric(item?.price_metric ?? null);
    setIntegrations((item?.integration_keys ?? []).join(", "));
    setUnitCost(item?.unit_cost ?? null);
    setFloor(item?.floor_price ?? null);
    setStarting(item?.starting_price ?? null);
    setRecommended(item?.recommended_price ?? null);
    setSku(item?.sku_cinch ?? "");
    setDescr(item?.descr ?? "");
    setCustDescr(item?.customer_facing_descr ?? "");
    save.reset();
  }, [open, item]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = () => {
    const body = {
      name: name.trim() || null,
      category,
      price_metric: priceMetric,
      integration_keys: integrations
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      unit_cost: unitCost,
      floor_price: floor,
      starting_price: starting,
      recommended_price: recommended,
      sku_cinch: sku.trim() || null,
      descr: descr.trim() || null,
      customer_facing_descr: custDescr.trim() || null,
    };
    save.mutate({ id: item?.id ?? null, body }, { onSuccess: onClose });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{item ? "Edit item" : "New item"}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Name" className="col-span-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Item name" />
          </Field>

          <Field label="Category">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ITEM_CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Price Metric">
            <Combobox
              options={metricOptions}
              value={priceMetric}
              onChange={setPriceMetric}
              placeholder="Select a price metric…"
              clearable
            />
          </Field>

          <Field label="Integrations (comma-separated keys)" className="col-span-2">
            <Input
              value={integrations}
              onChange={(e) => setIntegrations(e.target.value)}
              placeholder="e.g. hubspot, quickbooks"
            />
          </Field>

          <Field label="Unit Cost">
            <NumberInput value={unitCost} onChange={setUnitCost} prefix="$" />
          </Field>
          <Field label="Floor Price">
            <NumberInput value={floor} onChange={setFloor} prefix="$" />
          </Field>
          <Field label="Starting Price">
            <NumberInput value={starting} onChange={setStarting} prefix="$" />
          </Field>
          <Field label="Recommended Price">
            <NumberInput value={recommended} onChange={setRecommended} prefix="$" />
          </Field>

          <Field label="SKU (Cinch)" className="col-span-2">
            <Input value={sku} onChange={(e) => setSku(e.target.value)} />
          </Field>

          <Field label="Internal Description" className="col-span-2">
            <Textarea value={descr} onChange={(e) => setDescr(e.target.value)} rows={2} />
          </Field>
          <Field label="Customer-facing Description" className="col-span-2">
            <Textarea value={custDescr} onChange={(e) => setCustDescr(e.target.value)} rows={2} />
          </Field>
        </div>

        {save.error && (
          <Alert variant="danger">
            <AlertDescription>{writeError(save.error)}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={save.isPending || !name.trim()}>
            {save.isPending ? "Saving…" : item ? "Save changes" : "Create item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
